import { computed, ref } from "vue";
import { defineStore, storeToRefs } from "pinia";
import useMyFetch from "@stores/useMyFetch";
import handleError from "@utils/composables/handleError";
import { useMAudienceStore } from "@stores/mAudience/mAudience";
import { useMCampaignStore } from "@stores/mCampaign";
import { isEmpty } from "@utils/toolBox";

export const useEstimateStore = defineStore("estimate", () => {
  const mAudienceStore = useMAudienceStore();
  const {
    getMAudience,
    getMAudienceLocations,
    isGenderSegmentationActive,
    getMAudienceAgeBrackets,
    getMAudienceGenderSegments,
    getMAudienceInterests,
    getMAudienceLocales,
  } = storeToRefs(mAudienceStore);

  const mCampaignStore = useMCampaignStore();
  const { getCampaignAdAccount } = storeToRefs(mCampaignStore);

  const mAudienceReachEstimate = ref(0);
  const reachEstimateError = ref(false);

  // reusable functions for computed targets
  const computeAge = (type) =>
    computed(() =>
      getMAudienceAgeBrackets.value.length
        ? Math[type](...getMAudienceAgeBrackets.value.map((bracket) => Number(bracket[type])))
        : getMAudience.value?.t_age?.[type],
    );
  const computeLocations = (type, customMapping = null) =>
    computed(() =>
      getMAudienceLocations.value
        .filter((l) => l.type === type)
        .map((l) => (customMapping ? customMapping(l) : { key: l.key })),
    );
  const computeInterests = (type) =>
    computed(() =>
      getMAudienceInterests.value
        ?.filter((t) => t.target_type === type)
        ?.map((t) => ({ id: t.external_id || t.id, name: t.name })),
    );
  const computeLocales = () => computed(() => getMAudienceLocales?.value.map((locale) => locale.key) ?? []);

  const getMAudienceAgeMin = computeAge("min");
  const getMAudienceAgeMax = computeAge("max");

  const getMAudienceCities = computeLocations("city", (l) =>
    l.has_radius ? { key: l.key, radius: Number(l.radius), distance_unit: "kilometer" } : { key: l.key },
  );
  const getMAudienceZips = computeLocations("zip");
  const getMAudienceRegions = computeLocations("region");
  const getMAudienceCustomLocations = computeLocations("custom_location", (l) =>
    l.has_radius
      ? { latitude: l.latitude, longitude: l.longitude, radius: Number(l.radius), distance_unit: "kilometer" }
      : { latitude: l.latitude, longitude: l.longitude },
  );

  const getMAudienceInterestTargets = computeInterests("interests");
  const getMAudienceBehaviors = computeInterests("behaviors");
  const getMAudienceIndustries = computeInterests("industries");
  const getMAudienceIncome = computeInterests("income");
  const getMAudienceFamilyStatuses = computeInterests("family_statuses");
  const getMAudienceLifeEvents = computeInterests("life_events");
  const getMAudienceLocalesId = computeLocales();

  const getMAudienceGeoLocations = computed(() => {
    if (isEmpty(getMAudienceLocations.value)) return { countries: [getMAudience.value.t_geo?.country] };
    return {
      cities: getMAudienceCities.value,
      zips: getMAudienceZips.value,
      regions: getMAudienceRegions.value,
      custom_locations: getMAudienceCustomLocations.value,
    };
  });

  const getMAudienceGenders = computed(() => {
    const data = isGenderSegmentationActive.value
      ? {
          men: !isEmpty(getMAudienceGenderSegments.value.filter((s) => s.values?.men)),
          women: !isEmpty(getMAudienceGenderSegments.value.filter((s) => s.values?.women)),
        }
      : getMAudience.value.t_gender || {};

    return [...(data.men ? [1] : []), ...(data.women ? [2] : [])];
  });

  const getMAudienceTargets = computed(() => {
    return {
      age_min: getMAudienceAgeMin.value,
      age_max: getMAudienceAgeMax.value,
      geo_locations: getMAudienceGeoLocations.value,
      genders: getMAudienceGenders.value,
      interests: getMAudienceInterestTargets.value,
      behaviors: getMAudienceBehaviors.value,
      industries: getMAudienceIndustries.value,
      income: getMAudienceIncome.value,
      family_statuses: getMAudienceFamilyStatuses.value,
      life_events: getMAudienceLifeEvents.value,
      locales: getMAudienceLocalesId.value,
    };
  });
  const fetchReachEstimate = async () => {
    try {
      const currentTargets = JSON.stringify(getMAudienceTargets.value);
      const url = `/facebook/${getCampaignAdAccount.value}/reachestimate`;
      const { response, data } = await useMyFetch(url).post({ targeting_spec: currentTargets }).json();

      if (response.value.ok) {
        mAudienceReachEstimate.value = data.value?.data?.users_lower_bound || 0;
        reachEstimateError.value = mAudienceReachEstimate.value === 0;
      }
    } catch (err) {
      reachEstimateError.value = true;
      handleError(err);
    }
  };

  return {
    fetchReachEstimate,
    getMAudienceTargets,
    mAudienceReachEstimate,
    reachEstimateError,
  };
});
